import React, { useMemo, useState } from 'react';
import { Accordion, Card, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { cloneDeep, isEmpty, omit, set } from 'lodash';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AttributeCard from './AttributeCard';
import Loader from '../../../Components/Utility/Loader/Loader';
import ConfirmationPopup from '../../../Components/ModalPopup/ConfirmationPopup';
import _CustomTooltip from '../../../Components/Tooltip/CustomTooltip';
import { SystemMasterSelect } from '../../../Components/helper';
import { toasterMsg } from '../../../Components/Toaster/Toaster';
import { AppDispatch, useAppDispatch } from '../../../hooks/useAppDispatch';
import { useComponent } from '../../UserSetting/UsersLibrary/UserRights';
import {
  deletePartnerOverviewCustomAttribute as _deletePartnerOverviewCustomAttribute,
  fetchAllPCTypes as _fetchAllPCTypes,
  getPartnerOverviewCustomAttributeUsage as _getPartnerOverviewCustomAttributeUsage,
  getPartnerOverviewCustomAttributes as _getPartnerOverviewCustomAttributes,
  savePartnerOverviewCustomAttributes as _savePartnerOverviewCustomAttributes,
  setPartnerOverviewCustomAttributeActiveState as _setPartnerOverviewCustomAttributeActiveState,
  updatePartnerOverviewCustomAttribute as _updatePartnerOverviewCustomAttribute,
} from '../Store/SystemMasters.action';
import { PARTNER_OVERVIEW_ATTRIBUTE_LIMIT, PARTNER_OVERVIEW_DEFAULT_SURFACES, PARTNER_OVERVIEW_TYPE_OPTIONS, emptyAttribute, hasOptions } from './constants';
import {
  PartnerAttributeOption,
  PartnerAttributeUsage,
  PartnerOverviewAttribute,
  PartnerScopeType,
  PartnerTypeOption,
} from './partner-overview-attribute.types';

const CustomTooltip = _CustomTooltip as unknown as React.FC<{ tooltipText: React.ReactNode; children?: React.ReactNode }>;

type AttributeLike = Partial<Pick<PartnerOverviewAttribute, 'type' | 'options' | 'usedOptionIds'>> | undefined;

interface PartnerOverviewCustomAttributesState {
  Users: { logInRights: unknown };
}

interface ActionResult {
  success?: boolean;
  isInUse?: boolean;
}

type Thunk<TResult> = (dispatch: AppDispatch) => Promise<TResult>;
type CheckActionPermissionFn = (rights: unknown, section: string, module?: string, action?: string) => boolean;

const getPartnerOverviewCustomAttributes = _getPartnerOverviewCustomAttributes as unknown as (
  params: Record<string, unknown>,
) => Thunk<PartnerOverviewAttribute[] | null | undefined>;
const getPartnerOverviewCustomAttributeUsage = _getPartnerOverviewCustomAttributeUsage as unknown as () => Thunk<PartnerAttributeUsage | null>;
const fetchAllPCTypes = _fetchAllPCTypes as unknown as (
  params: Record<string, unknown>,
  isFromSystemMaster: boolean,
) => Thunk<PartnerScopeType[] | null | undefined>;
const savePartnerOverviewCustomAttributes = _savePartnerOverviewCustomAttributes as unknown as (payload: Array<Record<string, unknown>>) => Thunk<ActionResult>;
const updatePartnerOverviewCustomAttribute = _updatePartnerOverviewCustomAttribute as unknown as (payload: Record<string, unknown>) => Thunk<ActionResult>;
const setPartnerOverviewCustomAttributeActiveState = _setPartnerOverviewCustomAttributeActiveState as unknown as (payload: {
  attributeId: string;
  isActive: boolean;
}) => Thunk<ActionResult>;
const deletePartnerOverviewCustomAttribute = _deletePartnerOverviewCustomAttribute as unknown as (attributeId: string) => Thunk<ActionResult>;

/**
 * @description Whether one option may not be removed because a partner already selected it.
 *
 * usedOptionIds carries every option id held on a supplier or factory, including each id of a
 * multi-dropdown selection. An option with no id has never been saved, so it is always removable.
 */
export function isOptionDeletionBlocked(attribute: AttributeLike, optionIndex: number): boolean {
  const optionId = attribute?.options?.[optionIndex]?._id;
  if (!optionId) return false;
  return (attribute?.usedOptionIds ?? []).map(String).includes(String(optionId));
}

/**
 * @description Marks each option of a dropdown attribute as blank or filled, so the card can
 * point at the offending row rather than failing the whole attribute anonymously.
 *
 * Types without an option list are returned untouched: a text or date attribute can still carry
 * options left over from a type change, and those are dropped by toPayload rather than blocking
 * the save.
 */
export function markBlankOptions(entry: AttributeLike): PartnerAttributeOption[] {
  if (!hasOptions(entry?.type)) return entry?.options ?? [];
  return (entry?.options ?? []).map((option) => ({
    ...option,
    isEmpty: isEmpty((option?.keyName ?? '').trim()),
  }));
}

/**
 * @description Flags every option after the first that repeats a name already used on the same
 * attribute, comparing case-insensitively and ignoring surrounding spaces.
 *
 * Two options sharing a name are indistinguishable once they reach a supplier or factory
 * dropdown, and the saved value is an option id, so the wrong one can be selected with no way to
 * tell them apart afterwards. The first occurrence is left clean so the message points at the
 * row the user should change.
 */
export function markDuplicateOptions(entry: AttributeLike, options: PartnerAttributeOption[] | undefined): PartnerAttributeOption[] {
  if (!hasOptions(entry?.type)) return options ?? [];
  const seen = new Set<string>();
  return (options ?? []).map((option) => {
    const name = (option?.keyName ?? '').trim().toLowerCase();
    if (!name) return { ...option, isDuplicate: false };
    const isDuplicate = seen.has(name);
    seen.add(name);
    return { ...option, isDuplicate };
  });
}

/**
 * @description Partner Overview custom attribute master. Defines the supplier and factory custom
 * fields for a company, scoped by tier and product/component type, capped at
 * PARTNER_OVERVIEW_ATTRIBUTE_LIMIT live attributes shared across both surfaces.
 */
function PartnerOverviewCustomAttributes() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('modules/partner-overview');
  const CheckActionPermission = useComponent().CheckActionPermission as unknown as CheckActionPermissionFn;

  const {
    Users: { logInRights },
  } = useSelector((state: PartnerOverviewCustomAttributesState) => state);

  const addEditAccess = CheckActionPermission(logInRights, 'masters', 'customAttribute', 'addEdit');
  const deleteAccess = CheckActionPermission(logInRights, 'masters', 'customAttribute', 'delete');

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [attributes, setAttributes] = useState<PartnerOverviewAttribute[]>([]);
  const [pcTypes, setPcTypes] = useState<PartnerScopeType[]>([]);
  const [activeType, setActiveType] = useState<PartnerTypeOption | null>(PARTNER_OVERVIEW_TYPE_OPTIONS[0] ?? null);
  const [duplicateIndexes, setDuplicateIndexes] = useState<number[]>([]);
  const [pendingDelete, setPendingDelete] = useState<PartnerOverviewAttribute | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [usage, setUsage] = useState({
    used: 0,
    limit: PARTNER_OVERVIEW_ATTRIBUTE_LIMIT,
    remaining: PARTNER_OVERVIEW_ATTRIBUTE_LIMIT,
  });

  const supplierTypesByTier = useMemo(() => {
    const grouped: Record<string, PartnerScopeType[]> = {};
    (pcTypes ?? []).forEach((entry) => {
      const tier = entry?.tier ?? 'Tier1';
      const bucket = grouped[tier] ?? [];
      bucket.push(entry);
      grouped[tier] = bucket;
    });
    return grouped;
  }, [pcTypes]);

  const unsavedCount = attributes.filter((entry) => !entry?._id).length;
  const projectedUsed = usage.used + unsavedCount;
  const limitReached = projectedUsed >= (usage.limit ?? PARTNER_OVERVIEW_ATTRIBUTE_LIMIT);

  /** Loads attributes, slot usage and the tier-partitioned product/component type list. */
  const loadData = async () => {
    setIsLoading(true);
    const [list, slots, types] = await Promise.all([
      dispatch(getPartnerOverviewCustomAttributes({})),
      dispatch(getPartnerOverviewCustomAttributeUsage()),
      dispatch(fetchAllPCTypes({ isAllRecord: true }, true)),
    ]);
    if (slots) setUsage(slots);
    setPcTypes(types ?? []);
    if (list === null) {
      setIsLoading(false);
      return;
    }
    setAttributes(
      (list ?? []).map((entry) => ({
        ...entry,
        tierType: entry?.tierType ?? [],
        supplierType: (entry?.supplierType ?? []).map(String),
        appliesTo: entry?.appliesTo ?? [],
        options: entry?.options ?? [],
        isEmpty: false,
      })),
    );
    setIsLoading(false);
  };

  /** Toggles the accordion, fetching on open. */
  const toggleAccordion = async () => {
    const next = !isOpen;
    setIsOpen(next);
    if (next) await loadData();
  };

  /** Appends a blank attribute of the currently selected type, respecting the cap. */
  const addAttribute = () => {
    if (limitReached) {
      toasterMsg(t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.LIMIT_REACHED', { limit: usage.limit }), 'danger');
      return;
    }
    setAttributes((previous) => [...previous, emptyAttribute(activeType?.value ?? '')]);
  };

  /** Applies a field change to one attribute, supporting dotted paths for nested config. */
  const handleChange = (value: unknown, index: number, path: string) => {
    setAttributes((previous) => {
      const draft: PartnerOverviewAttribute[] = cloneDeep(previous);
      const target = draft[index];
      if (target) set(target, path, value);
      return draft;
    });
  };

  /** Appends a blank option to one dropdown attribute. */
  const handleAddOption = (index: number) => {
    setAttributes((previous) => {
      const draft: PartnerOverviewAttribute[] = cloneDeep(previous);
      const target = draft[index];
      if (target) target.options = [...(target.options ?? []), { keyName: '', isActive: true }];
      return draft;
    });
  };

  /** Applies a change to one field of one option. */
  const handleChangeOption = (value: string, index: number, optionIndex: number, field: 'keyName') => {
    setAttributes((previous) => {
      const draft: PartnerOverviewAttribute[] = cloneDeep(previous);
      const option = draft[index]?.options[optionIndex];
      if (option) option[field] = value;
      return draft;
    });
  };

  /**
   * Removes one option from a dropdown or multi-dropdown. An option a partner has already
   * selected is refused rather than removed: the saved value stores the option id, so dropping it
   * here would leave those partners holding an id that resolves to nothing.
   */
  const handleDeleteOption = (index: number, optionIndex: number) => {
    if (isOptionDeletionBlocked(attributes[index], optionIndex)) {
      toasterMsg(t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.DELETE_OPTION_IN_USE_BLOCKED'), 'danger');
      return;
    }
    setAttributes((previous) => {
      const draft: PartnerOverviewAttribute[] = cloneDeep(previous);
      draft[index]?.options.splice(optionIndex, 1);
      return draft;
    });
  };

  /** Flags rows that are incomplete or duplicate. Returns true when the list is safe to save. */
  const validate = () => {
    const seen = new Map<string, number>();
    const duplicates: number[] = [];

    const cloned: PartnerOverviewAttribute[] = cloneDeep(attributes);
    const draft = cloned.map((entry, index) => {
      const name = (entry?.keyName ?? '').trim().toLowerCase();
      if (name) {
        if (seen.has(name)) duplicates.push(index);
        else seen.set(name, index);
      }
      const options = markBlankOptions(entry);
      const markedOptions = markDuplicateOptions(entry, options);

      return {
        ...entry,
        options: markedOptions,
        isEmpty: isEmpty(name) || (entry?.tierType ?? []).length === 0 || (entry?.supplierType ?? []).length === 0 || options.some((option) => option?.isEmpty),
        hasDuplicateOption: markedOptions.some((option) => option?.isDuplicate),
      };
    });

    setAttributes(draft);
    setDuplicateIndexes(duplicates);
    return !draft.some((entry) => entry.isEmpty || entry.hasDuplicateOption) && duplicates.length === 0;
  };

  /** Builds the wire payload for one attribute, omitting config that its type does not use. */
  const toPayload = (entry: PartnerOverviewAttribute) => ({
    keyName: entry.keyName.trim(),
    aliasName: entry.aliasName ?? '',
    tierType: entry.tierType,
    supplierType: entry.supplierType,
    appliesTo: PARTNER_OVERVIEW_DEFAULT_SURFACES,
    options: hasOptions(entry.type)
      ? (entry.options ?? []).map((option) => ({
          ...omit(option, ['isEmpty', 'isDuplicate']),
          keyName: (option?.keyName ?? '').trim(),
        }))
      : [],
    textFieldConfig: entry.type === 'text' ? entry.textFieldConfig : undefined,
  });

  /** Persists new attributes and updates to existing ones. */
  const handleSave = async () => {
    if (!validate()) {
      toasterMsg(t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.FIX_ERRORS'), 'danger');
      return;
    }

    setIsSaving(true);
    const creates = attributes.filter((entry) => !entry?._id);
    const updates = attributes.filter((entry) => entry?._id);

    const results: Array<ActionResult | null | undefined> = await Promise.all([
      creates.length > 0
        ? dispatch(savePartnerOverviewCustomAttributes(creates.map((entry) => ({ ...toPayload(entry), type: entry.type }))))
        : Promise.resolve({ success: true }),
      ...updates.map((entry) => dispatch(updatePartnerOverviewCustomAttribute({ attributeId: entry._id, ...toPayload(entry) }))),
    ]);

    setIsSaving(false);
    if (results.every((result) => result?.success)) {
      toasterMsg(t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.SAVED'), 'success');
      await loadData();
    }
  };

  /**
   * Flips active state for a saved attribute. Deactivating hides it but keeps its slot.
   *
   * Per decision D19 deactivating is always available, in use or not: it only hides the field on
   * partner forms, and every saved value stays attached and returns on reactivation.
   */
  const handleToggleActive = async (item: PartnerOverviewAttribute) => {
    const result = await dispatch(
      setPartnerOverviewCustomAttributeActiveState({
        attributeId: item._id,
        isActive: !item.isActive,
      }),
    );
    if (result.success) await loadData();
  };

  /**
   * Opens the delete confirmation. An attribute that already holds values on a supplier or
   * factory is refused with an error instead, since deleting it would orphan those values.
   */
  const requestDelete = (item: PartnerOverviewAttribute, index: number) => {
    if (!item?._id) {
      setPendingDelete({ ...item, unsavedRowIndex: index });
      return;
    }
    if (item.isUsed) {
      toasterMsg(t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.DELETE_IN_USE_BLOCKED'), 'danger');
      return;
    }
    setPendingDelete(item);
  };

  /** Deletes the attribute awaiting confirmation and reloads the list. */
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    if (!pendingDelete?._id) {
      const { unsavedRowIndex } = pendingDelete;
      setAttributes((previous) => previous.filter((_unused, position) => position !== unsavedRowIndex));
      setPendingDelete(null);
      return;
    }
    setIsDeleting(true);
    const result = await dispatch(deletePartnerOverviewCustomAttribute(pendingDelete._id));
    setIsDeleting(false);
    setPendingDelete(null);
    if (result.success) {
      toasterMsg(t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.DELETED'), 'success');
      await loadData();
      return;
    }
    if (result.isInUse) {
      toasterMsg(t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.DELETE_IN_USE_BLOCKED'), 'danger');
      await loadData();
    }
  };

  /**
   * Lists only the attributes of the currently selected type, matching the sibling custom
   * attribute masters: the type selector chooses which set you are working on. The header counter
   * still reports usage across all four types, since the cap of 20 is shared.
   * Order follows seqNumber, matching the order the partner form renders them in.
   */
  const visibleAttributes = attributes
    .filter((entry) => entry?.type === activeType?.value)
    .sort((left, right) => (left?.seqNumber ?? 0) - (right?.seqNumber ?? 0));

  return (
    <>
      <Accordion className="mb-4 master-accordian custom-attribute-master">
        <Card>
          <Card.Header>
            <div className="title-container">
              <p>{t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.TITLE')}</p>
            </div>
            <div className="action-title-btns">
              {isOpen && (
                <span className="mr-16 text-bold f-14 text-nowrap">
                  {t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.USAGE', {
                    used: projectedUsed,
                    limit: usage.limit,
                  })}
                </span>
              )}
              <CustomTooltip tooltipText={isOpen ? t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.CLOSE') : t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.OPEN')}>
                <button className="btn d-inline-flex p-0" onClick={toggleAccordion} type="button">
                  <i className={`icon-new-down-arrow-icon f-24${isOpen ? ' icon-inverse' : ''}`} />
                </button>
              </CustomTooltip>
            </div>
          </Card.Header>

          {isOpen &&
            (isLoading ? (
              <Loader />
            ) : (
              <Card.Body>
                <div className="select-container">
                  <Select
                    options={PARTNER_OVERVIEW_TYPE_OPTIONS}
                    placeholder={t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.TYPE_SELECT_PLACEHOLDER')}
                    getOptionLabel={(option: PartnerTypeOption) => `${option.name}`}
                    getOptionValue={(option: PartnerTypeOption) => `${option.value}`}
                    name="type"
                    value={activeType}
                    styles={{
                      ...SystemMasterSelect,
                      menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
                    }}
                    onChange={(option: PartnerTypeOption | null) => setActiveType(option)}
                    isDisabled={!addEditAccess}
                    menuPlacement="auto"
                    menuPosition="fixed"
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                  />
                </div>

                <div className="common-custom-scrollbar partner-attribute-list">
                  {visibleAttributes.map((entry) => {
                    const index = attributes.indexOf(entry);
                    return (
                      <AttributeCard
                        key={entry._id || `new_${index}`}
                        item={entry}
                        index={index}
                        supplierTypesByTier={supplierTypesByTier}
                        addEditAccess={addEditAccess}
                        deleteAccess={deleteAccess}
                        onChange={handleChange}
                        onDelete={requestDelete}
                        onToggleActive={handleToggleActive}
                        onAddOption={handleAddOption}
                        onChangeOption={handleChangeOption}
                        onDeleteOption={handleDeleteOption}
                        duplicateIndexes={duplicateIndexes}
                      />
                    );
                  })}
                  {isEmpty(visibleAttributes) && <div className="text-center text-grey mt-4">{t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.NO_ATTRIBUTES')}</div>}
                </div>

                {addEditAccess && (
                  <CustomTooltip
                    tooltipText={
                      limitReached
                        ? t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.LIMIT_REACHED', { limit: usage.limit })
                        : t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.ADD_TOOLTIP')
                    }
                  >
                    <button type="button" onClick={addAttribute} className="btn btn-outline ml-auto mr-2 mt-16 p-0" disabled={limitReached} aria-label="Add">
                      <span className="icon-Add text-grey f-24" />
                    </button>
                  </CustomTooltip>
                )}

                <div className="select-container">
                  <div className="instruction-container mt-2 text-bold">
                    <span className="important-span">* </span>
                    {t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.INSTRUCTION', { limit: usage.limit })}
                  </div>
                </div>

                {addEditAccess && (
                  <div className="section-custom-save">
                    <CustomTooltip tooltipText={t('common:COMMON_LABEL.SAVE')}>
                      <button aria-label="Save" type="button" className="btn mr-2 p-0" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? <Spinner animation="border" /> : <i className="icon-Tick-1 f-24" />}
                      </button>
                    </CustomTooltip>
                  </div>
                )}
              </Card.Body>
            ))}
        </Card>
      </Accordion>

      {pendingDelete && (
        <ConfirmationPopup
          onOpen={Boolean(pendingDelete)}
          toggle={() => setPendingDelete(null)}
          title={t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.DELETE_TITLE')}
          message={
            pendingDelete?.keyName?.trim()
              ? t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.DELETE_MESSAGE', { name: pendingDelete.keyName })
              : t('PARTNER_OVERVIEW_CUSTOM_ATTRIBUTES.DELETE_UNNAMED_MESSAGE')
          }
          onConfirm={confirmDelete}
          loading={isDeleting}
        />
      )}
    </>
  );
}

export default PartnerOverviewCustomAttributes;
